import * as React from 'react';
import { Pencil, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatPostalAddress } from '@klareospace/pharos-lib-utils';
import { Badge, Card, CardContent, RowActionsMenu } from '@klareospace/pharos-lib-web-utils';
import type { RowAction } from '@klareospace/pharos-lib-web-utils';

import type { AddressBookEntryInput } from '../schemas/customer.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressCardProps {
  /** The customer's own full name, shown when the entry has no contact override. */
  contactFallbackName: string;
  entry: AddressBookEntryInput;
  onEdit: () => void;
  onRemove: () => void;
  onSetDefault: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Collapsed address-book entry: contact name, Default tag, full address, and a 3-dot menu. */
export function AddressCard({
  contactFallbackName,
  entry,
  onEdit,
  onRemove,
  onSetDefault,
}: AddressCardProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const hasOverrideContact = !entry.isCustomerContact && !!entry.contactName?.trim();
  const contactName = hasOverrideContact ? entry.contactName : contactFallbackName;
  const formattedAddress = formatPostalAddress(entry.address);
  const showContactPhone = hasOverrideContact && !!entry.contactPhone?.number;

  const actions: RowAction[] = [
    { icon: <Pencil className="h-4 w-4" />, label: tCommon('actions.edit'), onClick: onEdit },
    // "Set as default" is hidden (not disabled) once the entry is already default.
    ...(entry.isDefault
      ? []
      : [{ icon: <Star className="h-4 w-4" />, label: tCommon('actions.setAsDefault'), onClick: onSetDefault }]),
    {
      className: 'text-destructive',
      icon: <Trash2 className="h-4 w-4" />,
      label: tCommon('actions.remove'),
      onClick: onRemove,
    },
  ];

  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-4 p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{contactName}</span>
            {entry.isDefault && <Badge variant="secondary">{t('addressBook.default')}</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{formattedAddress}</p>
          {showContactPhone && entry.contactPhone && (
            <p className="text-sm text-muted-foreground">
              {entry.contactPhone.dialCode} {entry.contactPhone.number}
            </p>
          )}
        </div>
        <RowActionsMenu actions={actions} ariaLabel={t('table.columns.actions')} />
      </CardContent>
    </Card>
  );
}
